import React, { useCallback, useEffect } from 'react';
import KeepAlive from 'react-activation';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { createSelector } from 'reselect';
import { message } from 'antd';
import { debounce, reject } from 'lodash-es';
import classNames from 'classnames';

import {
  downloadGetSoftList,
  downloadUpdateSoftListState,
  downloadUpdateSoftListProgress,
  downloadShowSIM,
} from './actions/downloadTask';
import { updateIsSIMRunning, updateSIMProgress, updateSIMStatus } from './actions/asimstatus';
import { addWelcomeIdToDownload } from './actions/welcome';
import { setDownloadVisible, setPinPopCloseVisible } from './actions/layout';
import { logging } from './actions/log';
import {
  getLocalSoft,
  getSilentInstallSoft,
  getUpgradeSofts,
  removeUninstall,
  setCommandLine,
  setOtherInfo,
  setQuietInstallPath,
  setWelcomeSoftID,
  setUninstallFinishSofts,
} from './actions/native';
import success from './static/msg-success.svg';
import fail from './static/msg-fail.svg';

import { downloadAddSoft } from './actions/downloadTask';
import {
  addTempResidual,
  cancelUninstall,
  modifyResidualData,
  removeResidual,
  removeTempResidual,
  stopScanSoftwareResidual,
  removeConstrain,
} from './actions/uninstall';
import { updateUserInfo, getLoginClose, emptyLoginClose } from './actions/user';
import {
  removeDownloadImg,
  updateDownloadImgProgress,
  updateDownloadImgStatus,
} from './actions/wallpaper';
import { getWelcomeData } from './actions/welcome';
import { setFeedbackTask } from './actions/userFeedback';
import { CONTEXT, MICROCONTEXT } from './config';
import {
  APKS_NT_STATUS,
  SIM_DOWNLOAD_FAILED,
  SIM_DOWNLOAD_FINISHED,
  SIM_DOWNLOAD_ING,
  SIM_INSTALL_FAILED,
  SIM_INSTALL_FINISHED,
  SIM_INSTALL_ING,
  SIM_PREPARE,
  SIM_PREPARE_DONE,
  SIM_PREPARE_FAILED,
  SIM_PREPARING,
  SIM_UPDATE_INSTALL_FAILED,
  SIM_UPDATE_INSTALL_FINISHED,
  // 模拟器升级
  SIM_UPDATE_INSTALL_ING,
  SIM_VM_DESKTOP,
  SIM_VM_DESKTOP_FAILED,
  // SIM_DOWNLOAD_CANCELED,
  SIM_VM_RUN,
} from './constants/asimstatus';

import { MENUS, MENUS_SMB } from './constants/layout';
import { cacheKeys, GUI_MODE_INFO } from './constants/storageKeys';
import {
  WP_DOWNLOAD_CANCELED,
  WP_DOWNLOAD_FAILED,
  WP_DOWNLOAD_FINISHED,
} from './constants/wallpaper';

import { logNative, localGetItem, localSetItem } from './utils/utils';
import Layout from './layouts/Layout';
import { PCStoreJsBridge } from './nativeapi';
import ACategory from './pages/ACategory/ACategory';
import ADetail from './pages/ADetail/SoftDetail';
import ARecommend from './pages/ARecommend/ARecommend';
import Category from './pages/Category/Category';
import RankMore from './pages/Comm/RankMore';
import DataTest from './pages/DataTest/DataTest';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import ErrorPageEdu from './pages/ErrorPage/ErrorPageEdu';
import Game from './pages/Game/Game';
import RecoEducation from './pages/Education/EducationTabs/EducationTabs';
import GuessLikesMore from './pages/Recommend/GuessLikesMore';
import Recommend from './pages/Recommend/Recommend';
import RecoWork from './pages/RecoWork/RecoWork';
import SearchResult from './pages/SearchResult/SearchResult';
import Shop from './pages/Shop/Shop';
import Detail from './pages/Detail/SoftDetail';
import Special from './pages/Special/Special';
import SpecialList from './pages/Special/SpecialList';
import Topn from './pages/Topn/Topn';
import TopnMore from './pages/Topn/TopnMore';
import Uninstall from './pages/Uninstall/Uninstall';
import CollectUninstall from './pages/Uninstall/index';
import Update from './pages/Update/Update';
import WallPaperAuthorDetail from './pages/Wallpaper/WallPaperAuthorDetail';
import WallPaperAuthorList from './pages/Wallpaper/WallPaperAuthorList';
import WallpaperCategory from './pages/Wallpaper/WallpaperCategory/WallpaperCategory';
import WallpaperDetail from './pages/Wallpaper/WallpaperDetail';
// import WallpaperMine from './pages/Wallpaper/WallpaperMine/WallpaperMine';
import WallpaperMine from './pages/Wallpaper/WallpaperMine/index';
import WallPaperRank from './pages/Wallpaper/WallPaperRank';
import WallpaperRecommend from './pages/Wallpaper/WallpaperRecommend/WallpaperRecommend';

import Collection from './pages/Collection/Collection';
import EduDetail from './pages/Education/EduDetail/EduDetail';
import OtherPage from './pages/OtherPage/OtherPage';
import OtherTest from './pages/OtherPage/OtherTest';
// import MessageCore from './pages/MessageCore/MessageCore';
import OrderCenter from './pages/OrderCenter/OrderCenter';
import LoadingPage from './pages/LoadingPage/LoadingPage';
import Setting from './pages/Setting/Setting';
import Toast from './components/Toast/Toast';
import GameRankMore from './pages/Game/GameRank/RankMoreGame/RankMoreGame';
import Wallpaper from './pages/Wallpaper/Index';
import Discount from './pages/Discount/Discount';

const selectNeedShow = createSelector(
  state => state.WelcomApp,
  WelcomApp => WelcomApp.needShow,
);

const selectWelcomeIDs = createSelector(
  state => state.native,
  native => native.ids,
);

const selectLocalSofts = createSelector(
  state => state.native,
  native => native.installedSoft,
);

const selectSIMProgress = createSelector(
  state => state.asimstatus,
  asimstatus => asimstatus.progress,
);
const selectClearStatus = createSelector(
  state => state.uninstall,
  uninstall => uninstall.clearStatus,
);
const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectDownloadSofts = createSelector(
  state => state.downloadTask,
  downloadTask => downloadTask.softs,
);

function App() {
  const dispatch = useDispatch();
  const needShow = useSelector(selectNeedShow);
  const welcomeIDs = useSelector(selectWelcomeIDs);
  const localSofts = useSelector(selectLocalSofts);
  const progress = useSelector(selectSIMProgress); // 模拟器进度
  const clearStatus = useSelector(selectClearStatus); // 卸载残留清理进程状态
  const logInfoData = useSelector(selectLogInfo);
  const softs = useSelector(selectDownloadSofts); // 用于判断是否展示正在下載的数目

  /********************** 主应用传入子应用 中的一些方法 *******************/
  useEffect(() => {
    // 子应用可以调用主应用方法安装应用
    window.mainInstallSoft = softInfo => {
      dispatch(downloadAddSoft(softInfo));
    };
  }, [dispatch]);
  /*********************************************************************/

  useEffect(() => {
    if (localSofts.length === 0) {
      dispatch(getLocalSoft());
    }
  }, [dispatch, localSofts.length]);

  const updateProgress = useCallback(
    newProg => {
      if (newProg === 0 || newProg === 100) {
        dispatch(updateSIMProgress({ progress: newProg }));
      } else if (progress < newProg) {
        const progArr = [newProg, newProg + 1, newProg + 2, newProg + 3, newProg + 4];
        dispatch(updateSIMProgress({ progress: progArr[Math.floor(Math.random() * 5)] }));
      }
    },
    [dispatch, progress],
  );

  /**
   * 对于安卓应用通知状态的处理
   */
  useEffect(() => {
    PCStoreJsBridge.subscribeMessage(messageInfo => {
      if (APKS_NT_STATUS.indexOf(messageInfo.getMsgcategory()) > -1) {
        let retJson = {};
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          retJson = JSON.parse(ret);
        } catch (error) {
          logNative(`通知 SIM JSON 解析错误:${error}`, error);
        }
        let statusObj = {};
        switch (messageInfo.getMsgcategory()) {
          case 'APKS_NT_SIM_PREPARE':
            window.SIMTime.SIMRunStartTime = new Date().getTime();
            logNative('通知 APKS_NT_SIM_PREPARE 模拟器开始准备', retJson);
            dispatch(
              logging({
                category: 'Simulate',
                action: 'Start_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMRunStartTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(updateSIMStatus({ status: SIM_PREPARE }));
            updateProgress(0);
            break;
          case 'APKS_NT_SIM_DL_FETCH_INFO':
            logNative(
              '通知 APKS_NT_SIM_DL_FETCH_INFO 开始下载之前，即预下载准备阶段，比如获取下载URL信息',
              retJson,
            );
            break;
          case 'APKS_NT_SIM_DL_FETCH_INFO_DONE':
            logNative('通知 APKS_NT_SIM_DL_FETCH_INFO_DONE 开始下载之前，预下载完成', retJson);
            break;
          case 'APKS_NT_SIM_DL_START':
            window.SIMTime.SIMDLStartTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_DL_START 模拟器下载开始: ${window.SIMTime.SIMDLStartTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'D_Sd',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMDLStartTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            // 特殊处理
            dispatch(updateSIMStatus({ status: SIM_PREPARING }));
            updateProgress(1);
            break;
          case 'APKS_NT_SIM_DL_PROGRESS':
            logNative(
              `通知 APKS_NT_SIM_DL_PROGRESS 模拟器下载进度: ${parseFloat(retJson?.dlsz || 0) /
                parseFloat(retJson?.sz || 1)}`,
              retJson,
              true,
              true,
            );
            dispatch(updateSIMStatus({ status: SIM_DOWNLOAD_ING }));
            dispatch(
              updateSIMProgress({
                progress:
                  parseInt(
                    (parseFloat(retJson?.dlsz || 0) / parseFloat(retJson?.sz || 1)) * 70,
                    10,
                  ) + 5,
              }),
            );
            break;
          case 'APKS_NT_SIM_DL_DONE':
            window.SIMTime.SIMDownEndTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_INSTALL  模拟器下载结束: ${window.SIMTime.SIMDownEndTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'D_Ed',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMDownEndTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: !retJson.succ ? SIM_DOWNLOAD_FAILED : SIM_DOWNLOAD_FINISHED,
              }),
            );
            updateProgress(76);
            break;
          case 'APKS_NT_SIM_INSTALL':
            window.SIMTime.SIMInstallTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_INSTALL  模拟器开始安装: ${window.SIMTime.SIMInstallTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Si',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMInstallTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            dispatch(updateSIMStatus({ status: SIM_INSTALL_ING }));
            updateProgress(81);
            break;
          case 'APKS_NT_SIM_INSTALL_DONE':
            window.SIMTime.SIMInstEndTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_INSTALL_DONE  模拟器安装完成: ${window.SIMTime.SIMInstEndTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Ei',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMInstEndTime - window.SIMTime.SIMRunStartTime,
                  type: 'd',
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: retJson.succ !== 'true' ? SIM_INSTALL_FAILED : SIM_INSTALL_FINISHED,
              }),
            );
            updateProgress(86);
            break;
          case 'APKS_NT_SIM_UPGRADE':
            window.SIMTime.SIMUpdateInstStartTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_UPGRADE  模拟器升级安装开始: ${window.SIMTime
                .SIMUpdateInstStartTime - window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Si',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMUpdateInstStartTime - window.SIMTime.SIMRunStartTime,
                  type: 'u',
                },
              }),
            );
            dispatch(updateSIMStatus({ status: SIM_UPDATE_INSTALL_ING }));
            break;
          case 'APKS_NT_SIM_UPGRADE_DONE':
            window.SIMTime.SIMUpdateInstEndTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_SIM_UPGRADE_DONE  模拟器升级安装完成: ${window.SIMTime
                .SIMUpdateInstEndTime - window.SIMTime.SIMUpdateInstStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'I_Ei',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMUpdateInstEndTime - window.SIMTime.SIMUpdateInstStartTime,
                  type: 'u',
                },
              }),
            );
            dispatch(
              updateSIMStatus({
                status:
                  retJson.succ !== 'true' ? SIM_UPDATE_INSTALL_FAILED : SIM_UPDATE_INSTALL_FINISHED,
              }),
            );
            break;
          case 'APKS_NT_VM_RUN':
            window.SIMTime.SIMVMRunTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_VM_RUN  模拟器VM运行开始: ${window.SIMTime.SIMVMRunTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
              true,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'VM_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  time: window.SIMTime.SIMVMRunTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(updateSIMStatus({ status: SIM_VM_RUN }));
            updateProgress(91);
            break;
          case 'APKS_NT_VM_DESKTOP':
            window.SIMTime.SIMVMDesktopRunTime = new Date().getTime();
            logNative(
              `通知 APKS_NT_VM_DESKTOP  模拟器VM桌面启动结果: ${window.SIMTime.SIMVMDesktopRunTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'VM_DESKTOP_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMVMDesktopRunTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: retJson.succ !== 'true' ? SIM_VM_DESKTOP_FAILED : SIM_VM_DESKTOP,
              }),
            );
            // 模拟器启动失败重新获取一个本地列表  从而更改按钮（安装，更新，卸载）状态
            if (retJson.succ !== 'true') {
              dispatch(getLocalSoft());
              if (retJson?.code === -42006 || retJson?.code === `-42006`) {
                dispatch(downloadShowSIM('SIM_Need_Restart'));
              }
            }
            updateProgress(96);
            break;
          case 'APKS_NT_SIM_PREPARE_DONE':
            window.SIMTime.SIMRunEndTime = +new Date();
            logNative(
              `通知 APKS_NT_SIM_PREPARE_DONE  模拟器准备结束: ${window.SIMTime.SIMRunEndTime -
                window.SIMTime.SIMRunStartTime}`,
              retJson,
            );
            dispatch(
              logging({
                category: 'Simulate',
                action: 'End_Simulate',
                label: '',
                param2: {
                  launchid: window.SIMTime.SIMRunStartTime,
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMRunEndTime - window.SIMTime.SIMRunStartTime,
                },
              }),
            );

            dispatch(
              updateSIMStatus({
                status: retJson.succ !== 'true' ? SIM_PREPARE_FAILED : SIM_PREPARE_DONE,
              }),
            );
            // 模拟器启动失败重新获取一个本地列表  从而更改按钮（安装，更新，卸载）状态
            if (retJson.succ !== 'true') {
              dispatch(getLocalSoft());
            }
            if (progress <= 100) {
              dispatch(updateSIMProgress({ progress: 100 }));
            }

            // 更新是否启动模拟器
            dispatch(updateIsSIMRunning({ isSIMRunning: retJson.succ === 'true' }));
            break;
          case 'APKS_NT_UNINSTALL_START':
            logNative(`通知 APKS_NT_UNINSTALL_START`, retJson);
            break;
          case 'APKS_NT_UNINSTALL_DONE':
            message.success({
              content: '卸载成功',
              icon: (
                <img className={classNames('ant-message-custom-img')} src={success} alt="error" />
              ),
            });
            logNative(`通知 APKS_NT_UNINSTALL_DONE`, retJson);
            break;
          case 'APKS_NT_RUN_START':
            window.SIMTime.SIMApkRunStartTime = +new Date();
            logNative(`通知 APKS_NT_RUN_START apk软件启动开始`, retJson);
            // apk开始运行
            dispatch(
              logging({
                category: 'Simulate',
                action: 'Start_App',
                label: '',
                param2: {
                  packageName: retJson.pkg,
                  versionCode: retJson.vc,
                  versionName: retJson.vn,
                },
              }),
            );
            break;
          case 'APKS_NT_RUN_DONE':
            window.SIMTime.SIMApkRunEndTime = +new Date();
            logNative(
              `通知 APKS_NT_RUN_DONE apk软件启动结束: ${window.SIMTime.SIMApkRunEndTime -
                window.SIMTime.SIMApkRunStartTime}`,
              retJson,
            );
            // apk结束运行
            dispatch(
              logging({
                category: 'Simulate',
                action: 'End_App',
                label: '',
                param2: {
                  msg: retJson.msg,
                  code: retJson.code,
                  status: retJson.succ,
                  time: window.SIMTime.SIMApkRunEndTime - window.SIMTime.SIMApkRunStartTime,
                  packageName: retJson.pkg,
                  versionCode: retJson.vc,
                  versionName: retJson.vn,
                },
              }),
            );
            break;
          case 'APKS_PC_NT_UPGRADE_LIST':
            logNative(
              `通知 APKS_PC_NT_UPGRADE_LIST PC/安卓List 获取更新耗时: ${new Date().getTime() -
                Number(window.getUpdateStartTime)}ms, 可更新: ${retJson.data &&
                retJson.data.length}个`,
              retJson,
              true,
            );
            dispatch(getUpgradeSofts(retJson.data || []));
            break;
          default:
            logNative(`通知 default 客户端未处理通知 ${messageInfo}`);
            break;
        }
      }
    });
  }, []);

  /**
   * 对于PC应用，客户端通知状态的处理,
   * 此处针对下载任务通知，做一个过滤下载进度通知太多太频繁就过滤一些
   */
  useEffect(() => {
    PCStoreJsBridge.subscribeMessage(
      debounce(messageInfo => {
        // 下载进度变化通知
        if (messageInfo.getMsgcategory() === 'ON_DL_PROGRESS') {
          const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
          try {
            const retJson = JSON.parse(ret);
            logNative(`通知 ON_DL_PROGRESS  PC软件下载进度`, retJson, true, true);
            dispatch(downloadUpdateSoftListProgress(retJson));
          } catch (error) {
            logNative(`通知 ON_DL_PROGRESS JSON 解析错误:${error}`, error);
          }
        }
      }, 150),
    );
  }, []);

  /**
   * 对于PC应用，客户端通知状态的处理
   */
  useEffect(() => {
    PCStoreJsBridge.subscribeMessage(messageInfo => {
      // 下载任务状态变更通知
      if (messageInfo.getMsgcategory() === 'ON_DL_STATE_CHG') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(
            `通知 ON_DL_STATE_CHG  软件下载状态 ${JSON.stringify(retJson)}`,
            retJson,
            true,
            true,
          );
          dispatch(downloadUpdateSoftListState(retJson));

          /*********************************/
          // 在收到状态通知为安装结束的时候去获取一次本地列表，这样处理会让获取本地列表次数减少
          if (retJson && retJson.attr && retJson.attr.status && retJson.attr.status === 'exitq') {
            dispatch(downloadGetSoftList());
            dispatch(getLocalSoft());
          }
          /*********************************/
        } catch (error) {
          logNative(`通知 ON_DL_STATE_CHG JSON 解析错误:${error}`, error);
        }
      }

      // 下载安装完成变化通知
      if (messageInfo.getMsgcategory() === 'ON_DL_END_INSTALL') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          retJson.attr = {
            ...retJson.attr,
            act: 'eInstSucc',
          };
          logNative(`通知 ON_DL_END_INSTALL  PC软件安装进度`, retJson, true, true);
          dispatch(downloadUpdateSoftListState(retJson));
        } catch (error) {
          logNative(`通知 ON_DL_END_INSTALL PC软件安装进度 JSON解析错误:${error}`, error);
        }
      }

      // 安卓模拟器在安装中客户端给通知H5进行提示
      if (messageInfo.getMsgcategory() === 'ON_SIM_INSTALL') {
        logNative(`通知 ON_SIM_INSTALL  模拟器已经开始安装无法中断`);
        Toast.error({ content: '模拟器已开始安装，无法中断' });
      }
      // 用户登录、退出登录通知
      if (messageInfo.getMsgcategory() === 'PCM_MESSAGE_NOTIFY_LOGIN') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(`通知 PCM_MESSAGE_NOTIFY_LOGIN 登录信息变更刷新`, retJson);
          dispatch(
            logging({
              category: 'R',
              action: 'Login_Update',
              label: '',
              param2: { userInfo: retJson },
            }),
          );
          dispatch(updateUserInfo(retJson));
        } catch (error) {
          logNative(`通知 PCM_MESSAGE_NOTIFY_LOGIN JSON 解析错误:${error}`, error);
        }
      }

      // 启动session信息
      if (messageInfo.getMsgcategory() === 'ON_START_INFO') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(`通知 ON_START_INFO 商店启动信息：`, {
            ...retJson,
            isDStore: retJson.leas_runtime === 'independent',
          });
          localStorage.setItem(
            cacheKeys.START_INFO,
            JSON.stringify({
              ...retJson,
              isDStore: retJson.leas_runtime === 'independent',
            }),
          );
        } catch (error) {
          logNative(`通知 ON_START_INFO JSON 解析错误:${error}`, error);
        }
      }

      // 启动命令行
      if (messageInfo.getMsgcategory() === 'ON_START_COMMOANDLINE') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        const cmd = ret.trim();
        if (cmd) {
          logNative(`通知 ON_START_COMMOANDLINE setCommandLine ${cmd}`);
          dispatch(setCommandLine(cmd));
        }
      }

      // 商店打开着  调用命令跳入某个页面（目前为命令跳入详情页面，并开始下载应用）
      if (messageInfo.getMsgcategory() === 'ON_WINDOW_WM_COPYDATA') {
        const ret = JSON.parse(PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto()));
        if (ret && ret.data.trim()) {
          logNative(`通知 ON_WINDOW_WM_COPYDATA setCommandLine ${ret.data.trim()}`);
          dispatch(setCommandLine(ret.data.trim()));
        }
        // 处理托盘关闭时是否存在下载任务弹窗
        if (ret?.wparam === 1 && ret?.dwdata === 16) {
          setTimeout(() => {
            PCStoreJsBridge.closeWindow();
          }, 100);
          // logNative(`通知触发托盘点击关闭时存在下载任务`);
          // const downloadSofts = [];
          // softs.forEach(soft => {
          //   if (soft.attr.status === 'dl' || soft.attr.status === 'pause') {
          //     downloadSofts.push(
          //       soft.apk && soft.apk.pkg ? soft.apk.pkg : soft.pc ? soft.pc.SoftID : '',
          //     );
          //   }
          // });
          // if (downloadSofts && downloadSofts.length > 0) {
          //   logNative(`托盘存在下载任务调起商店弹窗`);
          //   dispatch(setPinPopCloseVisible(true));
          // }
        }
      }

      if (messageInfo.getMsgcategory() === 'APPSTORE_SYSCOMMAND') {
        // const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
      }

      if (messageInfo.getMsgcategory() === 'APPSTORE_WELCOME_SOFTSID') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        dispatch(setWelcomeSoftID(ret));
      }

      // // 下载进度变化通知
      // if (messageInfo.getMsgcategory() === 'APPSTORE_DOWNLOADPROGRESS') {
      //   const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
      //   try {
      //     const retJson = JSON.parse(ret);
      //     logNative(`通知 APPSTORE_DOWNLOADPROGRESS  PC软件下载进度`, retJson, true, true);
      //     dispatch(updateDownloadProgress(retJson));
      //   } catch (error) {
      //     logNative(`通知 APPSTORE_DOWNLOADPROGRESS JSON 解析错误:${error}`, error);
      //   }
      // }

      // // 下载状态变化通知
      // if (messageInfo.getMsgcategory() === 'APPSTORE_DOWNLOADDING_STATUS') {
      //   const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
      //   try {
      //     const retJson = JSON.parse(ret);
      //     // 新逻辑只处理我定义的状态 2020-7-28 11:00:43
      //     if (ALL_STATUS.indexOf(retJson.status) > -1) {
      //       dispatch(updateDownloadSoftStatus(retJson));
      //     }

      //     logNative(`通知 APPSTORE_DOWNLOADDING_STATUS PC软件安装 状态`, retJson);
      //     if (
      //       retJson.status === INSTALL_FAILED ||
      //       retJson.status === INSTALL_FINISHED ||
      //       retJson.status === DOWNLOAD_CANCELED
      //     ) {
      //       dispatch(removeDownload(retJson.SoftID));
      //     }
      //     if (retJson.status === INSTALL_FINISHED) {
      //       dispatch(getLocalSoft());
      //     }
      //   } catch (error) {
      //     logNative(`通知 APPSTORE_DOWNLOADDING_STATUS JSON 解析错误:${error}`, error);
      //   }
      // }

      // 获取更新列表
      if (messageInfo.getMsgcategory() === 'APPSTORE_GETUPGRADE_SOFTLIST') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(
            `通知 APPSTORE_GETUPGRADE_SOFTLIST 获取可更新软件耗时: ${new Date().getTime() -
              Number(window.getUpdateStartTime)}`,
            retJson,
            true,
            true,
          );
          let uniqRet = [];
          if (retJson.length && retJson.length > 0) {
            // 去重
            // uniqRet = uniqBy(retJson, e => e.SoftID);
            uniqRet = retJson;
          }
          dispatch(getUpgradeSofts(uniqRet));

          // 启动商店后，js开始执行到获取到更新列表所用时长打桩
          if (!window.PC_Store_UpdateList_Get_End) {
            window.PC_Store_UpdateList_Get_End = +new Date();
            dispatch(
              logging({
                category: 'R',
                action: 'R_UpdateList',
                label: '',
                param2: {
                  time: window.PC_Store_UpdateList_Get_End - window.PC_STORE_START_JS_LOAD_END,
                },
              }),
            );
          }
        } catch (error) {
          logNative(`通知 APPSTORE_GETUPGRADE_SOFTLIST JSON 解析错误:${error}`, error);
        }
      }

      // 壁纸下载进度变化通知
      if (messageInfo.getMsgcategory() === 'APPSTORE_DOWNWALLPAPER_PROGRESS') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          let retJson = JSON.parse(ret);
          retJson = { ...retJson, id: retJson.id };
          logNative(`通知 APPSTORE_DOWNWALLPAPER_PROGRESS  壁纸下载进度`, retJson, true, true);
          dispatch(updateDownloadImgProgress({ wallpaper: retJson }));
        } catch (error) {
          logNative(`通知 APPSTORE_DOWNWALLPAPER_PROGRESS JSON 解析错误:${error}`, error);
        }
      }

      // 壁纸下载状态更新
      if (messageInfo.getMsgcategory() === 'APPSTORE_DOWNWALLPAPER_STATUS') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          let retJson = JSON.parse(ret);
          retJson = { ...retJson, id: retJson.id };
          logNative(
            `通知 APPSTORE_DOWNWALLPAPER_STATUS  壁纸下载状态更新${JSON.stringify(retJson)}`,
            retJson,
            true,
            true,
          );

          // if (retJson.status === WP_DOWNLOAD_FAILED) {
          //   Toast.error({ content: '操作失败，请重试！' });
          // }
          if (retJson.status === WP_DOWNLOAD_FAILED || retJson.status === WP_DOWNLOAD_CANCELED) {
            dispatch(removeDownloadImg({ wallpaper: retJson }));
          } else {
            dispatch(updateDownloadImgStatus({ wallpaper: retJson }));
          }

          if (retJson.status === WP_DOWNLOAD_FINISHED) {
            setTimeout(() => {
              dispatch(removeDownloadImg({ wallpaper: retJson }));
            }, 700);
          }
        } catch (error) {
          logNative(`通知 APPSTORE_DOWNWALLPAPER_STATUS JSON 解析错误:${error}`, error);
        }
      }
      if (messageInfo.getMsgcategory() === 'APPSTORE_START_FORCE_UNINSTALL') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        const retJson = JSON.parse(ret);
        logNative(`通知 APPSTORE_START_FORCE_UNINSTALL 开始强力删除${ret}`, retJson, true, true);
      }

      // 软件卸载残留清理结果通知
      if (messageInfo.getMsgcategory() === 'APPSTORE_REQ_DELETE_RESIDUAL_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        const retJson = JSON.parse(ret);
        try {
          logNative(
            `通知 APPSTORE_REQ_DELETE_RESIDUAL_RESULT 删除残留状态${ret}`,
            retJson,
            true,
            true,
          );
          // 卸载残留清理
          const { succ, softGUID } = retJson;
          if (succ) {
            dispatch(
              modifyResidualData({
                softGUID,
                status: clearStatus.CLEAR_SUCC,
              }),
            );
          } else {
            dispatch(
              modifyResidualData({
                softGUID,
                status: clearStatus.CLEAR_ERR,
              }),
            );
          }
        } catch (errors) {
          logNative(`通知 APPSTORE_REQ_DELETE_RESIDUAL_RESULT JSON 解析错误${errors}`, errors);
        }
      }
      // 软件卸载-扫描残留文件进度通知
      if (messageInfo.getMsgcategory() === 'APPSTORE_REQ_SOFTWARE_RESIDUAL_PERCENT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        const retJson = JSON.parse(ret);
        const { softGUID, percent } = retJson;
        try {
          logNative(
            `通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_PERCENT 卸载扫描残留进度${ret}`,
            retJson,
            true,
            true,
          );
          dispatch(
            modifyResidualData({
              softGUID,
              percent,
            }),
          );
        } catch (errors) {
          logNative(`通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_PERCENT JSON 解析错误${errors}`, errors);
        }
      }
      // 卸载扫描 -扫描残留文件结果通知
      if (messageInfo.getMsgcategory() === 'APPSTORE_REQ_SOFTWARE_RESIDUAL_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        const retJson = JSON.parse(ret);
        const { softGUID, code } = retJson;
        try {
          logNative(
            `通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_RESULT 卸载状态${ret}`,
            retJson,
            true,
            true,
          );
          dispatch(
            modifyResidualData({
              softGUID,
              percent: 100,
            }),
          );
          // 扫描结果--没有残留
          if (code === 2) {
            dispatch(
              modifyResidualData({
                softGUID,
                hasResidual: false,
              }),
            );
          }
          // 扫描结果-扫描失败
          if (code === 3) {
            dispatch(modifyResidualData({ softGUID, status: clearStatus.CLEAR_ERR }));
            dispatch(removeTempResidual({ softGUID }));
            dispatch(removeResidual({ softGUID }));
            return;
          }
          if (code !== 2 && code !== 3) {
            dispatch(
              modifyResidualData({
                softGUID,
                totalNum: retJson.residualResult.residualInfo.totalNum,
                totalSize: retJson.residualResult.residualInfo.totalSize,
                residualResult: retJson.residualResult.residualInfo,
              }),
            );
          }
        } catch (errors) {
          logNative(`通知 APPSTORE_REQ_SOFTWARE_RESIDUAL_RESULT JSON 解析错误${errors}`, errors);
        }
      }
      // 软件卸载-开始扫描状态消息
      if (messageInfo.getMsgcategory() === 'APPSTORE_START_SCAN_RESIDUAL') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          const { softGUID } = retJson;
          logNative(`通知 APPSTORE_START_SCAN_RESIDUAL 扫描状态${ret}`, softGUID, true, true);
          // 8s后改变状态为监听状态
          const timer = setTimeout(() => {
            // 卸载残留
            dispatch(
              addTempResidual({
                softGUID,
                ...retJson,
                hasResidual: true,
                percent: 0,
                status: 'SCANING',
              }),
            );
            clearTimeout(timer);
          }, 8000);
          // 扫描状态超过30s 停止扫描
          const timers = setTimeout(() => {
            dispatch(
              stopScanSoftwareResidual({
                softGUID,
                ...retJson,
              }),
            );
            clearTimeout(timers);
          }, 30000);
        } catch (error) {
          logNative(`通知 APPSTORE_START_SCAN_RESIDUAL JSON 解析错误${error}`, error);
        }
      }
      // 软件卸载-卸载结果
      if (messageInfo.getMsgcategory() === 'APPSTORE_UNINSTALL_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          dispatch(
            logging({
              category: 'R',
              action: 'T_Cleaner_Result',
              label: '',
              param2: {
                status: retJson?.UninstallStatus === 2 ? 1 : 0,
                softid: retJson?.softid ?? '',
                guid: retJson?.softGUID,
                type: retJson?.type ?? '',
                source: 'Mysofts',
                source_p: logInfoData.source_p,
                source_pp: logInfoData.source_pp,
              },
            }),
          );
          logNative(`通知 APPSTORE_UNINSTALL_RESULT 卸载状态${ret}`, retJson, true, true);
          // 卸载残留
          if (retJson.hasResidual && retJson.UninstallStatus === 2) {
            // 剔除因为返回扫描状态临时假如的卸载残留软件

            const residualInfo = {
              residualResult: retJson.residualInfo,
              status: 'init',
              totalNum: retJson.residualInfo.totalNum,
              totalSize: retJson.residualInfo.totalSize,
              hasResidual: true,
              percent: 100,
            };
            if (retJson.residualInfo.totalSize > 200 * 1024 * 1024) {
              // 移除不存在残留的扫描状态
              dispatch(removeTempResidual({ softGUID: retJson.softGUID }));
            } else {
              dispatch(
                addTempResidual({
                  softGUID: retJson.softGUID,
                  ...residualInfo,
                }),
              );
            }

            // 不再获取安装列表，成功直接从当前列表移除
            dispatch(removeUninstall(retJson));
            dispatch(cancelUninstall(retJson));
          }
          // 卸载成功
          if (
            !retJson.hasResidual &&
            retJson.UninstallStatus === 2 &&
            !Reflect.has(retJson, 'mode')
          ) {
            message.success({
              content: '卸载成功',
              icon: (
                <img className={classNames('ant-message-custom-img')} src={success} alt="error" />
              ),
            });
            // 不再获取安装列表，成功直接从当前列表移除
            dispatch(removeUninstall(retJson));
            // 移除不存在残留的扫描状态
            dispatch(removeTempResidual({ softGUID: retJson.softGUID }));
            dispatch(cancelUninstall(retJson));
          }
          // 强力卸载卸载成功
          if (!retJson.hasResidual && retJson.UninstallStatus === 2 && retJson.mode === 2) {
            // 强力卸载以后要展示三秒的动画
            dispatch(
              logging({
                category: 'R',
                action: 'Uninstall_Fail_Result',
                label: '',
                param2: {
                  softid: retJson?.softid ?? '',
                  guid: retJson?.softGUID,
                  source: 'Mysofts',
                  source_p: logInfoData.source_p,
                  source_pp: logInfoData.source_pp,
                  type: retJson?.type ?? '',
                  status: retJson?.UninstallStatus === 2 ? 1 : 0,
                },
              }),
            );

            dispatch(setUninstallFinishSofts(retJson));
            setTimeout(() => {
              // 不再获取安装列表，成功直接从当前列表移除
              dispatch(removeUninstall(retJson));
              // 移除不存在残留的扫描状态
              dispatch(removeTempResidual({ softGUID: retJson.softGUID }));
              dispatch(cancelUninstall(retJson));
              // 将展示动画的标识清空
              dispatch(setUninstallFinishSofts({}));
            }, 3000);
          }

          // 卸载失败或取消卸载
          if (retJson.UninstallStatus === 3) {
            dispatch(cancelUninstall(retJson));

            // 处理强力卸载卸载失败的情况
            if (Reflect.has(retJson, 'mode') && retJson.mode === 2) {
              message.error({
                content: '强制删除失败，请手动删除',
                icon: (
                  <img className={classNames('ant-message-custom-img')} src={fail} alt="success" />
                ),
              });
              dispatch(removeConstrain(retJson));
            }
          }
          // 其他卸载结果 --- 回复状态-至卸载
          if (retJson.UninstallStatus !== 2 && retJson.UninstallStatus !== 3) {
            dispatch(removeUninstall(retJson));
          }
        } catch (error) {
          logNative(`通知 APPSTORE_UNINSTALL_RESULT JSON 解析错误${error}`, error);
        }
      }

      // 用户反馈-反馈提交结果
      if (messageInfo.getMsgcategory() === 'APPSTORE_REQ_SUBMIT_FEEDBACK_RESULT') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          const retJson = JSON.parse(ret);
          logNative(
            `通知 APPSTORE_REQ_SUBMIT_FEEDBACK_RESULT 反馈提交状态${ret}`,
            retJson,
            true,
            true,
          );
          // 保存反馈状态
          dispatch(setFeedbackTask(retJson));
        } catch (error) {
          logNative(`通知 setFeedbackTask JSON 解析错误${error}`, error);
        }
      }

      // smb定制预装，smb模式更改
      if (messageInfo.getMsgcategory() === 'LSTORE_NOTI_BIZPARAM_MODIFIED') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          let bizParam = {};
          let guiMode = '0';
          let guiModeInfo = {};

          if (window.external && window.external.get_gui_mode && window.external.get_bizparam) {
            bizParam =
              window.external.get_bizparam() || JSON.parse(window.external.bizparam || '{}');
            guiMode = window.external.get_gui_mode() || '';
            guiModeInfo = { ...bizParam, guiMode };
          }
          const retJson = JSON.parse(ret);
          if (!(retJson && retJson.succ)) {
            // message.info('设置失败！');
          }
          logNative(`通知 LSTORE_NOTI_BIZPARAM_MODIFIED smb模式更改`, {
            retJson,
            guiModeInfo,
          });
          dispatch(
            logging({
              category: 'R',
              action: 'System_Type_Update',
              label: '',
              param2: {
                retJson,
                ...guiModeInfo,
              },
            }),
          );
          localSetItem(GUI_MODE_INFO, JSON.stringify(guiModeInfo));
        } catch (error) {
          logNative(`通知 LSTORE_NOTI_BIZPARAM_MODIFIED smb模式更改 JSON 解析错误:${error}`, error);
        }
      }

      // 是否关闭了登录弹窗
      if (messageInfo.getMsgcategory() === 'PCM_MESSAGE_NOTIFY_LOGIN_WINDOW_CLOSE') {
        const ret = PCStoreJsBridge.arrayToUTF8String(messageInfo.getMessageproto());
        try {
          let retJson = JSON.parse(ret);

          // retJson = { ...retJson, id: retJson.id };
          logNative(
            `通知 PCM_MESSAGE_NOTIFY_LOGIN_WINDOW_CLOSE  登录弹窗关闭成功`,
            retJson,
            true,
            true,
          );
          dispatch(getLoginClose(retJson));
        } catch (error) {
          logNative(
            `通知 PCM_MESSAGE_NOTIFY_LOGIN_WINDOW_CLOSE JSON 登录弹窗关闭解析错误:${error}`,
            error,
          );
        }
      }
    });
  }, []);

  // 进入页面就获取相关信息 路径 下载路径 静默安装路径 用户userToken
  useEffect(() => {
    const notNewUser = localStorage.getItem('WELCOME_USER');
    const userTypeIds = localStorage.getItem('WELCOME_USER_TYPE');
    dispatch(
      getWelcomeData({
        ids: notNewUser !== 'NO' ? [] : (userTypeIds && JSON.parse(userTypeIds)) || ['1'],
      }),
    );
    PCStoreJsBridge.getOtherInfo().then(res => {
      dispatch(setOtherInfo(res));
    });
    PCStoreJsBridge.getQuietInstallPath().then(res => {
      dispatch(setQuietInstallPath({ ...res }));
    });

    // 更新用户userToken
    const userTokenLS = { data: localStorage.getItem(cacheKeys.X_LESYNC_TOKEN) };
    PCStoreJsBridge.getUserTokenInfo(userTokenLS).then(res => {
      if (!res) {
        localStorage.setItem(cacheKeys.X_LESYNC_TOKEN, '');
      }
    });
  }, []);

  useEffect(() => {
    if (welcomeIDs && welcomeIDs.length > 0) {
      dispatch(addWelcomeIdToDownload(welcomeIDs));
      dispatch(setDownloadVisible(true));
    }
  }, [welcomeIDs]);

  /**
   * 讲一些初始化信息放入startInfo 中
   */
  const getInitInfoSaveToStartInfo = useCallback(() => {
    // 进入页面获取 卸载磁铁和右键菜单开关状态  并且写入到startInfo 中去
    PCStoreJsBridge.getPCStoreTileStatus().then(res => {
      const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}');
      localStorage.setItem(
        cacheKeys.START_INFO,
        JSON.stringify({
          ...startInfo,
          storeTileInfo: res.succ ? { enable: res.enable, cloudEnable: res.cloudEnable } : null,
        }),
      );
    });
    // 这里设置右键菜单开关状态
    PCStoreJsBridge.getRightMenuInfo().then(res => {
      const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}');
      localStorage.setItem(
        cacheKeys.START_INFO,
        JSON.stringify({
          ...startInfo,
          rightMenuInfo: res.succ ? res : null,
        }),
      );
    });
  });

  useEffect(() => {
    // 进入页面就获取支持静默安装的应用
    dispatch(getSilentInstallSoft());

    // 晚一秒去获取 卸载磁铁和右键菜单开关状态
    // setTimeout(() => {
    getInitInfoSaveToStartInfo();
    // }, 1000);
    // 商店是否支持大小缩放
    if (window.PcstoreCanChangeSize) {
      window.pcmAllowStretchWindow && window.pcmAllowStretchWindow();
    }
    // 获取安装列表
    dispatch(downloadGetSoftList());
  }, []);

  // // 8.5.10 处理异常退出登录后商店还是存着用户信息的bug
  // useEffect(() => {
  //   const index = setTimeout(() => {
  //     const login_status = window.CallNativeMethod('get_login_status');
  //     if (login_status !== '1') {
  //       localStorage.setItem(cacheKeys.X_LESYNC_TOKEN, '');
  //       localStorage.setItem(cacheKeys.USER_INFO, '');
  //     }
  //     clearTimeout(index);
  //   }, 1000);
  // }, []);

  // 进来就报一个桩，表示页面js加载完成。
  useEffect(() => {
    dispatch(
      logging({
        category: 'R',
        action: 'PCSTORE_JS_Load_End',
        param2: {
          source: 'PCSTORE_JS_Load_End',
          PCStoreStartBegin: window.PC_STORE_START_BEGIN, // 点击启动商店时间点
          PCStoreHtmlLoadEnd: window.PC_STORE_START_HTML_LOAD_END, // html下载完成时间点
          PCStoreJsLoadEnd: window.PC_STORE_START_JS_LOAD_END, // JS css下载完成时间点
          startToHtmlLoad: window.PC_STORE_START_HTML_LOAD_END - window.PC_STORE_START_BEGIN, // 从点击启动 到 html下载完成所用时间
          jsLoadTime: window.PC_STORE_START_JS_LOAD_END - window.PC_STORE_START_HTML_LOAD_END, // JS下载完成所用时间
          startToJsLoadTime: window.PC_STORE_START_JS_LOAD_END - window.PC_STORE_START_BEGIN, // 从点击启动 到 JS下载完成所用时间
        },
      }),
    );
  }, []);

  // // test
  // useEffect(() => {
  //   if (!window.external.is_net_connected) {
  //     localStorage.setItem('RELEASE_NOTES_VERSION', '8.0.10.0418');
  //     localStorage.setItem(
  //       'X-Lesync-Token',
  //       'eyJhbGciOiJIUzI1NiJ9.eyJqdGkiOiJmNzI1Mjg2Yy04NTBjLTRjZTMtYmQ0Yi03ZGRkOGQ5MTAyNzYiLCJpYXQiOjE2NTY0OTEwMTIsImV4cCI6MTY1NzA5NTgxMiwiaXNzIjoibGVzeW5jIiwic3ViIjoie1wiSURcIjoxMDA3OTQzOTU4OCxcImRldmljZUlEXCI6XCJNUDFRRzZTTlwiLFwidXNlcm5hbWVcIjpcIjE1NzAxMjExMzM3XCJ9In0.LWgtN89_q43xHS9L-LdcDoi9Uegod0ZB3zYtZVH0wBQ',
  //     );
  //     localStorage.setItem('WELCOME_USER_TYPE', '["8"]');
  //     localStorage.setItem('WELCOMEPOPTIME', '20220629');
  //     localStorage.setItem(
  //       'USER_INFO',
  //       '{"IsDeviceId":false,"bshownewmember":1,"headimgurl":"https://i.app.lefile.cn/uc_server/data/avatar/00161/52768/11066/MR6UV/39588_profilehead.jpg?t=1615276811273","lidNickName":"hero","membernickName":"15701211337","realname":"15701211337","sensitiveNickName":"hero","showquicklogin":false,"st":"ZAgAAAAAAAGE9MTAwNzk0Mzk1ODgmYj0yJmM9NCZkPTE2ODYwJmU9Rjc5QTNDNTQ5MTYzNzk2MEI3MURFQTU0M0Q4RDQ2MDcxJmg9MTY1NjQ4ODg3MDA2OCZpPTMwMjQwMCZvPU1QMVFHNlNOJnA9bXV1aWQmcT0wJnVzZXJuYW1lPTE1NzAxMjExMzM3JmlsPWNuWx8Kz31PgrC0Z8Xltk1rOg","status":true,"uid":"10079439588","uname":"15***37"}',
  //     );
  //     localStorage.setItem('FORUM_GUIDE_SHOW', '1');
  //     localStorage.setItem('IS_VT_ENABLE', '1');
  //     localStorage.setItem(
  //       'INSTALL_PARAM',
  //       'deviceModel=Lenovo%20Legion%20Y9000X%202020&lenovoId=10079439588&userId=15701211337&sn=MP1QG6SN&deviceId=5c80b6fffeb0&deviceType=MAC&clientVersion=8.0.0.329&appVersion=8.2.0.0630&appVersionCode=82000',
  //     );
  //     localStorage.setItem(
  //       'GUI_MODE_INFO',
  //       '{"btoken":"eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzbWJPcHRpb25zIjoiMSIsImJ1c2luZXNzS2V5IjoiIiwiY2hhbm5lbCI6InByZWxvYWQiLCJmcm9tIjoicGNtc3RvcmUiLCJ2ZXJzaW9uTmFtZSI6IjguMC4wLjMyOSIsImV4cCI6MjI4NzIwODA4MCwidmVyc2lvbkNvZGUiOiI4LjAuMC4zMjkifQ.en9jHblxQ4mWoMliksyQStuUzEgD8CBdrVaMbz6jGRU","businessCode":null,"shieldMenu":null,"guiMode":"1"}',
  //     );
  //     localStorage.setItem('WELCOME_USER', 'NO');
  //     localStorage.setItem('MENU_POINT', '{"discover":1}');
  //     localStorage.setItem(
  //       'START_INFO',
  //       '{"ClientVersion":"8.0.0.329","MainLoadEndTime":"1656491011759","MainLoadStartTime":"1656491010027","SN":"MP1QG6SN","SessionID":"FFB6805C-8162-B0CE-8A8B-D801BDD23E42","autoclean":0,"leas_channel":"preload","leas_runtime":"embeded","ludp_uuid":"CE631F7F-207E-11EA-8102-7C8AE1208D2A","rightKeyMenuStatus":1,"spanday":0,"isDStore":false,"storeTileInfo":{"enable":true,"cloudEnable":true},"rightMenuInfo":{"enable":true,"cloudEnable":true}}',
  //     );
  //     localStorage.setItem('EDUSTAGE', '4');
  //     localStorage.setItem(
  //       'LOCALMACHINE_INFO',
  //       '{"MAC":"5c80b6fffeb0","MT":"Lenovo Legion Y9000X 2020","OS":"win10","OSbit":"64","SN":"f356954f1797b87a834884dbb89fef389d55704e7f51c52c2aa2b06bde92354b","UUID":"CE631F7F-207E-11EA-8102-7C8AE1208D2A","leas_runtime":"embeded","osBuild":"10.0.19041.1741"}',
  //     );
  //     localStorage.setItem(
  //       'REGISTER_TOKEN',
  //       'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJvcyI6IndpbjEwIiwiY2giOiI4LjAuMTAuMDQxOCIsIm10IjoiTGVub3ZvIExlZ2lvbiBZOTAwMFggMjAyMCIsIm9zQml0IjoiNjQiLCJzbiI6ImYzNTY5NTRmMTc5N2I4N2E4MzQ4ODRkYmI4OWZlZjM4OWQ1NTcwNGU3ZjUxYzUyYzJhYTJiMDZiZGU5MjM1NGIiLCJleHAiOjE2NTcyODE0OTIsInV1aWQiOiJDRTYzMUY3Ri0yMDdFLTExRUEtODEwMi03QzhBRTEyMDhEMkEiLCJtYWMiOiI1YzgwYjZmZmZlYjAifQ.lkOxVRt0uCPfu6oA6xmB1hmlP32vML0I4SK7M_CZ3Eg',
  //     );
  //   }
  // }, []);

  // 进来就开始获取系统模式
  const getGuiMode = useCallback(() => {
    if (window.external && window.external.get_bizparam && window.external.get_gui_mode) {
      let bizParam = window.external.get_bizparam() || JSON.parse(window.external.bizparam || '{}');
      let guiMode = window.external.get_gui_mode() || '';
      let guiModeInfo = { ...bizParam, guiMode };
      localSetItem(GUI_MODE_INFO, JSON.stringify(guiModeInfo));

      logNative(`进入页面，就开始获取系统模式 结果： guiMode:${guiMode}`);
      dispatch(
        logging({
          category: 'R',
          action: 'Read_Budata',
          label: '',
          param2: {
            businessCode: bizParam.businessCode, //商店系统模式code
            shieldMenu: bizParam.shieldMenu, // shieldMenu： []需要隐藏的菜单
            guiMode, // guiMode：用户选着1/0 0位默认模式
            ...guiModeInfo,
          },
        }),
      );
      return guiModeInfo;
    }
    return null;
  }, []);

  // 初始启动，获取一次gui_mode   系统模式
  useEffect(() => {
    getGuiMode();
  }, []);

  // 根据gui_mode系统模式   确认默认路由
  const getDefaultRoute = useCallback(() => {
    // 从数据库中获取gui_mode系统模式
    let guiModeInfo = JSON.parse(localGetItem(GUI_MODE_INFO) || '{}');
    if (!guiModeInfo || !guiModeInfo.guiMode) {
      // 从数据库中获取gui_mode系统模式失败，就直接接口获取
      guiModeInfo = getGuiMode();
    }
    return guiModeInfo?.businessCode === 'SMB' ? MENUS_SMB[0] : MENUS[0];
  }, []);

  let defaultRoute = getDefaultRoute();
  return (
    <Router>
      <Layout>
        <Switch>
          <Route
            exact
            path={`${CONTEXT}/`}
            render={() => <Redirect to={`${CONTEXT}/${defaultRoute}`} />}
          />
          <Route
            exact
            path={`/embedded/all`}
            render={() => <Redirect to={`${CONTEXT}/${defaultRoute}`} />}
          />
          <Route exact path={`${CONTEXT}/recommend`} render={props => <Recommend />} />
          <Route path={`${CONTEXT}/education`}>
            <RecoEducation />
          </Route>
          <Route path={`${CONTEXT}/edudetail`}>
            <EduDetail />
          </Route>
          <Route exact path={`${CONTEXT}/work`} render={props => <RecoWork />} />
          <Route path={`${CONTEXT}/discount`}>
            <Discount />
          </Route>
          <Route path={`${CONTEXT}/game`}>
            <Game />
          </Route>
          <Route exact path={`${CONTEXT}/category`} render={props => <Category />} />
          <Route exact path={`${CONTEXT}/topn`} component={Topn} />
          <Route exact path={`${CONTEXT}/topnmore/:code/:title`} component={TopnMore} />
          <Route exact path={`${CONTEXT}/shop`} component={Shop} />
          <Route exact path={`${CONTEXT}/update`} render={props => <Update />} />
          {/* <Route exact path={`${CONTEXT}/uninstall`} render={props => <Uninstall />} /> */}
          <Route path={`${CONTEXT}/uninstall`} render={props => <CollectUninstall />} />
          <Route exact path={`${CONTEXT}/detail/soft/:id/:bizInfo`} component={Detail} />
          {/* <Route exact path={`${CONTEXT}/search/soft`} render={props => <SearchResult />} /> */}
          <Route exact path={`${CONTEXT}/searchresult`} render={props => <SearchResult />} />
          <Route exact path={`${CONTEXT}/guesslikesmore`} component={GuessLikesMore} />
          <Route exact path={`${CONTEXT}/rankmore`} component={RankMore} />
          <Route exact path={`${CONTEXT}/dt`} component={DataTest} />
          <Route exact path={`${CONTEXT}/arecommend`} render={props => <ARecommend />} />
          <Route exact path={`${CONTEXT}/acategory`} component={ACategory} />
          <Route exact path={`${CONTEXT}/adetail/:pn/:vc/:bizInfo`} component={ADetail} />
          <Route exact path={`${CONTEXT}/special`} render={props => <Special />} />
          <Route exact path={`${CONTEXT}/speciallist`} render={props => <SpecialList />} />
          <Route path={`${CONTEXT}/wallpaper`}>
            <Wallpaper />
          </Route>
          <Route exact path={`${CONTEXT}/wpdetail`} component={WallpaperDetail} />
          <Route exact path={`${CONTEXT}/wpauthorlist`} render={props => <WallPaperAuthorList />} />
          <Route
            exact
            path={`${CONTEXT}/wpauthordetail`}
            render={props => <WallPaperAuthorDetail />}
          />
          <Route exact path={`${CONTEXT}/wprank`} render={props => <WallPaperRank />} />

          <Route exact path={`${CONTEXT}/otherpage`} render={props => <OtherPage />} />
          <Route exact path={`${CONTEXT}/othertest`} render={props => <OtherTest />} />
          <Route path={`${CONTEXT}/collection`}>
            <Collection />
          </Route>
          <Route path={`${CONTEXT}/erroredu`}>
            <ErrorPageEdu />
          </Route>

          <Route path={`${CONTEXT}${MICROCONTEXT}`} render={props => <></>} />
          <Route
            exact
            path={`${CONTEXT}${MICROCONTEXT}`}
            render={() => <Redirect to={`${CONTEXT}${MICROCONTEXT}${MICROCONTEXT}/forumlist`} />}
          />
          {/* <Route exact path={`${CONTEXT}/messagecore`} render={() => <MessageCore />} /> */}
          <Route path={`${CONTEXT}/ordercenter`}>
            <OrderCenter />
          </Route>
          <Route path={`${CONTEXT}/setting`}>
            <Setting />
          </Route>
          <Route path={`${CONTEXT}/loadingpage`}>
            <LoadingPage />
          </Route>
          <Route path={`${CONTEXT}/error`}>
            <ErrorPage />
          </Route>
          <Route exact path={`${CONTEXT}/rankmore_game`} render={() => <GameRankMore />} />
          <Redirect to={`${CONTEXT}/${defaultRoute}`} />
        </Switch>
      </Layout>
    </Router>
  );
}

export default App;
